import { useState, useEffect, useCallback, useRef } from 'react';
import { Stack, Center, Loader, Alert, Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { AlertTriangle, DollarSign } from 'react-feather';
import type { CohortWithDetails } from '../../../types';
import { useEmailToggle } from '../../../hooks';
import {
  cohortCoachesApi,
  type CohortCoachWithDetails,
  type UncoveredTimeSlotInfo,
  type ScheduleGridSession,
} from '../../../services/api/cohortCoaches';
import { cohortCompensationApi } from '../../../services/api/cohortCompensation';
import { CoachesSubNav, type CoachesSubTab } from './coaches/CoachesSubNav';
import { CoachesZeroState } from './coaches/CoachesZeroState';
import { CoachesInCohortTable } from './coaches/CoachesInCohortTable';
import { AddPotentialCoachesModal } from './coaches/AddPotentialCoachesModal';
import { UploadPartnerCoachesModal } from './coaches/UploadPartnerCoachesModal';
import { SendAvailabilityRequestModal } from './coaches/SendAvailabilityRequestModal';
import { CoachDetailModal } from './coaches/CoachDetailModal';
import { CoachScheduleView } from './coaches/CoachScheduleView';
import { UncoveredTimeSlotsCallout } from './coaches/UncoveredTimeSlotsCallout';
import { CompensationSubTab } from './coaches/CompensationSubTab';

interface CoachesTabProps {
  cohort: CohortWithDetails;
  isActive?: boolean;
  onEditCohortDetails?: () => void;
  /** MED-1355: the list total after each load, so the tab badge matches the list. */
  onCoachCountChange?: (count: number) => void;
}

export function CoachesTab({
  cohort,
  isActive,
  onEditCohortDetails,
  onCoachCountChange,
}: CoachesTabProps) {
  // A ref, so a new callback from the parent does not start a new fetch.
  const onCoachCountChangeRef = useRef(onCoachCountChange);
  useEffect(() => {
    onCoachCountChangeRef.current = onCoachCountChange;
  }, [onCoachCountChange]);
  const [coaches, setCoaches] = useState<CohortCoachWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  // MED-1306: accept the sub-tab from the URL so a link can land on Compensation.
  // Without this a link opened Assignments and the reader had to know where to click
  // next. Read once on mount: the sub-tab is local state after that, so re-reading the
  // param would fight the user every time they switched by hand.
  const [activeSubTab, setActiveSubTabState] = useState<CoachesSubTab>(() => {
    const requested = new URLSearchParams(window.location.search).get('coachTab');
    return requested === 'compensation' || requested === 'schedule' ? requested : 'assignments';
  });
  // MED-1383: write the sub-tab back to the URL, so a reload or a shared link stays on
  // Compensation. Assignments is the default and drops the param.
  const [, setSearchParams] = useSearchParams();
  const setActiveSubTab = useCallback(
    (tab: CoachesSubTab) => {
      setActiveSubTabState(tab);
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (tab === 'assignments') next.delete('coachTab');
          else next.set('coachTab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [selectedCoaches, setSelectedCoaches] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Uncovered time slots data (MED-454) — shared between both views
  const [uncoveredSlots, setUncoveredSlots] = useState<UncoveredTimeSlotInfo[]>([]);
  const [uncoveredSessions, setUncoveredSessions] = useState<ScheduleGridSession[]>([]);

  // MED-663: Track whether compensation is configured for gating
  const [isCompensationConfigured, setIsCompensationConfigured] = useState<boolean | null>(null);

  // MED-579: Email toggle gating
  const { isEnabled: isEmailEnabled } = useEmailToggle(cohort.id);
  // MED-778: Feature toggle supersedes per-feature toggles
  const coachFeaturesOff = cohort.coach_features_enabled === false;
  const isAvailabilityEmailDisabled = coachFeaturesOff || !isEmailEnabled('coaches', 'coach_availability_request');

  // Modals
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [uploadModalOpened, { open: openUploadModal, close: closeUploadModal }] =
    useDisclosure(false);
  const [availabilityModalOpened, { open: openAvailabilityModal, close: closeAvailabilityModal }] =
    useDisclosure(false);
  const [detailModalOpened, { open: openDetailModal, close: closeDetailModal }] = useDisclosure(false);
  const [selectedCoachUserId, setSelectedCoachUserId] = useState<string | null>(null);

  // Fetch coaches in cohort
  const fetchCoaches = useCallback(async () => {
    try {
      setLoading(true);
      const result = await cohortCoachesApi.list(cohort.id, { page, limit: 25 });
      setCoaches(result.data);
      setTotalPages(result.pagination?.totalPages || 1);
      setTotalCount(result.pagination?.total || 0);
      onCoachCountChangeRef.current?.(result.pagination?.total || 0);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load coaches',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [cohort.id, page]);

  // Fetch uncovered slots data (MED-454)
  const fetchUncoveredData = useCallback(async () => {
    try {
      const data = await cohortCoachesApi.getScheduleGrid(cohort.id);
      setUncoveredSlots(data.uncovered);
      setUncoveredSessions(data.sessions);
    } catch {
      // Non-critical — silently fail
    }
  }, [cohort.id]);

  // MED-663: Check if compensation is configured
  const fetchCompensationStatus = useCallback(async () => {
    try {
      const result = await cohortCompensationApi.getConfig(cohort.id);
      setIsCompensationConfigured(result.config !== null);
    } catch {
      // Non-critical — assume not configured
      setIsCompensationConfigured(false);
    }
  }, [cohort.id]);

  // Only fetch when tab becomes active
  useEffect(() => {
    if (isActive) {
      fetchCoaches();
      fetchUncoveredData();
      fetchCompensationStatus();
    }
  }, [isActive, fetchCoaches, fetchUncoveredData, fetchCompensationStatus]);

  // MED-918 AC14: reconcile `selectedCoaches` against the refreshed coaches
  // list. When a coach is removed via the row action, the table refetches and
  // the removed coach drops out of `coaches` — but their user ID would
  // otherwise linger in `selectedCoaches`, causing the "X selected" header to
  // overcount and the Send Availability Request modal to pass a stale
  // recipient list to the server. The reconciliation runs against every
  // refresh (not just removal) so any path that drops a coach — current or
  // future — is handled uniformly. Backend hardening from AC16 still kicks
  // in as defense-in-depth if a user ID slips through (e.g. race condition).
  useEffect(() => {
    setSelectedCoaches(prev => {
      const liveIds = new Set(coaches.map(c => c.user_id));
      const filtered = prev.filter(id => liveIds.has(id));
      // Avoid an unnecessary state update — keeps React from re-rendering
      // children that depend on selection.
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [coaches]);

  // Handle adding coaches from modal. MED-1355: the modal shows the one
  // success toast, with the count, so this does not show a second one.
  const handleCoachesAdded = useCallback(() => {
    closeAddModal();
    fetchCoaches();
    fetchUncoveredData();
  }, [closeAddModal, fetchCoaches, fetchUncoveredData]);

  // Handle availability request sent
  const handleAvailabilityRequestSent = useCallback(() => {
    closeAvailabilityModal();
    fetchCoaches();
    setSelectedCoaches([]);
    notifications.show({
      title: 'Availability Request Sent',
      message: 'Availability requests have been sent to the selected coaches',
      color: 'green',
    });
  }, [closeAvailabilityModal, fetchCoaches]);

  // Handle coach removal (EC-35: optional thank you email)
  const handleRemoveCoach = useCallback(
    async (coachUserId: string, sendThankYou?: boolean) => {
      try {
        await cohortCoachesApi.remove(cohort.id, coachUserId, sendThankYou);
        fetchCoaches();
        fetchUncoveredData();
        notifications.show({
          title: 'Coach Removed',
          message: sendThankYou
            ? 'Coach has been removed and thank you email sent'
            : 'Coach has been removed from the cohort',
          color: 'green',
        });
      } catch {
        notifications.show({
          title: 'Error',
          message: 'Failed to remove coach',
          color: 'red',
        });
      }
    },
    [cohort.id, fetchCoaches, fetchUncoveredData],
  );

  // Toggle coach selection
  const handleToggleCoachSelection = useCallback((coachUserId: string) => {
    setSelectedCoaches(prev =>
      prev.includes(coachUserId) ? prev.filter(id => id !== coachUserId) : [...prev, coachUserId],
    );
  }, []);

  // Select all coaches
  const handleSelectAllCoaches = useCallback(() => {
    if (selectedCoaches.length === coaches.length) {
      setSelectedCoaches([]);
    } else {
      setSelectedCoaches(coaches.map(c => c.user_id));
    }
  }, [coaches, selectedCoaches.length]);

  // Open coach detail modal
  const handleCoachClick = useCallback((coachUserId: string) => {
    setSelectedCoachUserId(coachUserId);
    openDetailModal();
  }, [openDetailModal]);

  // Mark coach contract as signed (EC-36)
  const handleMarkContractSigned = useCallback(
    async (coachUserId: string) => {
      try {
        await cohortCoachesApi.markContractSigned(cohort.id, coachUserId);
        fetchCoaches();
        notifications.show({
          title: 'Contract Signed',
          message: 'Coach contract has been marked as received',
          color: 'green',
        });
      } catch {
        notifications.show({
          title: 'Error',
          message: 'Failed to update contract status',
          color: 'red',
        });
      }
    },
    [cohort.id, fetchCoaches],
  );

  if (loading && coaches.length === 0) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const hasCoaches = coaches.length > 0 || totalCount > 0;

  return (
    <Stack gap="lg">
      {/* Sub-navigation */}
      <CoachesSubNav
        activeSubTab={activeSubTab}
        onSubTabChange={setActiveSubTab}
        onAddPotentialCoaches={openAddModal}
        onSendAvailabilityRequest={openAvailabilityModal}
        hasSelectedCoaches={selectedCoaches.length > 0}
        selectedCount={selectedCoaches.length}
        isCompensationConfigured={isCompensationConfigured}
        isEmailDisabled={isAvailabilityEmailDisabled}
        onUploadPartnerCoaches={openUploadModal}
        showUploadPartnerCoaches={Boolean(cohort.partner_id)}
      />

      {/* MED-663 AC14: Compensation not configured callout */}
      {activeSubTab === 'assignments' && isCompensationConfigured === false && (
        <Alert
          icon={<AlertTriangle size={18} />}
          color="warningAmber"
          variant="light"
          title="Compensation must be configured before sending availability requests."
        >
          <Button
            size="xs"
            variant="outline"
            color="warningAmber"
            leftSection={<DollarSign size={14} />}
            onClick={() => setActiveSubTab('compensation')}
            mt="xs"
          >
            Configure Compensation
          </Button>
        </Alert>
      )}


      {/* Uncovered Time Slots Callout (AC23-26: replaces stat cards, shows in both views) */}
      {uncoveredSlots.length > 0 && (
        <UncoveredTimeSlotsCallout uncovered={uncoveredSlots} sessions={uncoveredSessions} />
      )}

      {/* Main Content */}
      {activeSubTab === 'assignments' && (
        <>
          {hasCoaches ? (
            <CoachesInCohortTable
              coaches={coaches}
              selectedCoaches={selectedCoaches}
              onToggleSelection={handleToggleCoachSelection}
              onSelectAll={handleSelectAllCoaches}
              onRemoveCoach={handleRemoveCoach}
              onCoachClick={handleCoachClick}
              onMarkContractSigned={handleMarkContractSigned}
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={setPage}
              loading={loading}
            />
          ) : (
            <CoachesZeroState onAddCoaches={openAddModal} />
          )}
        </>
      )}

      {activeSubTab === 'schedule' && (
        <CoachScheduleView
          cohort={cohort}
          onCoachClick={handleCoachClick}
        />
      )}

      {activeSubTab === 'compensation' && (
        <CompensationSubTab
          cohort={cohort}
          hasCoaches={hasCoaches}
          // MED-1024: refresh the gate's `isCompensationConfigured` from the
          // server after a comp save/unlock so the Send Availability Request
          // button and the "must configure" callout reflect current state
          // without a hard browser refresh (AC1–AC4).
          onConfigurationChange={fetchCompensationStatus}
        />
      )}

      {/* Modals */}
      <AddPotentialCoachesModal
        opened={addModalOpened}
        onClose={closeAddModal}
        cohort={cohort}
        onCoachesAdded={handleCoachesAdded}
      />

      <UploadPartnerCoachesModal
        opened={uploadModalOpened}
        onClose={closeUploadModal}
        cohort={cohort}
        onUploadComplete={() => {
          fetchCoaches();
          fetchUncoveredData();
        }}
      />

      <SendAvailabilityRequestModal
        opened={availabilityModalOpened}
        onClose={closeAvailabilityModal}
        cohort={cohort}
        selectedCoachUserIds={selectedCoaches}
        coaches={coaches.filter(c => selectedCoaches.includes(c.user_id))}
        onRequestSent={handleAvailabilityRequestSent}
        onEditCohortDetails={onEditCohortDetails}
      />

      <CoachDetailModal
        opened={detailModalOpened}
        onClose={closeDetailModal}
        cohortId={cohort.id}
        coachUserId={selectedCoachUserId}
        onUpdate={fetchCoaches}
      />
    </Stack>
  );
}

export default CoachesTab;
